import { StyleSheet, css } from 'aphrodite';
import { type FC, useEffect, useMemo, useState } from 'react';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { Input } from '../../../web/components-v2/Input';
import { SPACE } from '../../../web/components-v2/Theme/theme';
import type { FocusedLeadContact } from '../data';
import { IconPlus, IconX } from '../ui/icons';

/**
 * Add / Edit Contact dialog for the LEFT column.
 *
 * The same component handles both `mode: 'add'` and `mode: 'edit'` because
 * the legacy contact form behaves identically for the two — only the title
 * changes and the submit verb. Pre-populates from `contact` when editing.
 *
 * The dialog is purely presentational: it gathers form state and hands a
 * `ContactDraft` back to the parent, which dispatches the mutation. No
 * react-query, no MobX in this file.
 */
export type ContactPhoneDraft = {
	value: string;
	label: string;
};

export type ContactDraft = {
	firstName: string;
	lastName: string;
	jobTitle: string;
	emails: string[];
	phones: ContactPhoneDraft[];
};

export type ContactDialogMode = 'add' | 'edit';

export type ContactDialogProps = {
	open: boolean;
	mode: ContactDialogMode;
	onOpenChange: (open: boolean) => void;
	contact?: FocusedLeadContact;
	onSave: (draft: ContactDraft) => void;
	saving?: boolean;
};

const PHONE_LABELS = ['Work', 'Mobile', 'Cell', 'Home', 'Direct', 'Office', 'Fax', 'Other'];
const DEFAULT_LABEL = 'Work';

const splitDisplayName = (name: string): { firstName: string; lastName: string } => {
	const parts = (name ?? '').trim().split(/\s+/);
	if (parts.length <= 1) {
		return { firstName: parts[0] ?? '', lastName: '' };
	}
	return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

const buildInitialDraft = (mode: ContactDialogMode, contact?: FocusedLeadContact): ContactDraft => {
	if (mode === 'edit' && contact) {
		const { firstName: derivedFirst, lastName: derivedLast } = splitDisplayName(contact.name);
		return {
			firstName: contact.firstName ?? derivedFirst ?? '',
			lastName: contact.lastName ?? derivedLast ?? '',
			jobTitle: contact.title ?? '',
			emails: contact.emailAddresses.length ? contact.emailAddresses.map(e => e.value) : [''],
			phones: contact.phones.length
				? contact.phones.map(p => ({ value: p.standard, label: p.label ?? DEFAULT_LABEL }))
				: [{ value: '', label: DEFAULT_LABEL }],
		};
	}
	return {
		firstName: '',
		lastName: '',
		jobTitle: '',
		emails: [''],
		phones: [{ value: '', label: DEFAULT_LABEL }],
	};
};

export const ContactDialog: FC<ContactDialogProps> = ({ open, mode, onOpenChange, contact, onSave, saving }) => {
	const [draft, setDraft] = useState<ContactDraft>(() => buildInitialDraft(mode, contact));

	// Re-seed the form whenever the dialog re-opens or switches contact.
	useEffect(() => {
		if (!open) return;
		setDraft(buildInitialDraft(mode, contact));
	}, [open, mode, contact]);

	const title = mode === 'edit' ? 'Edit contact' : 'Add contact';
	const submitLabel = mode === 'edit' ? 'Save' : 'Add';

	// Need at least one of: name, email, or phone — match legacy behavior
	// where the contact endpoint rejects fully blank submissions.
	const canSave = useMemo(() => {
		if (saving) return false;
		const hasName = !!(draft.firstName.trim() || draft.lastName.trim());
		const hasEmail = draft.emails.some(e => e.trim().length > 0);
		const hasPhone = draft.phones.some(p => p.value.trim().length > 0);
		return hasName || hasEmail || hasPhone;
	}, [draft, saving]);

	const updateEmail = (index: number, value: string) => {
		setDraft(prev => ({ ...prev, emails: prev.emails.map((e, i) => (i === index ? value : e)) }));
	};

	const removeEmail = (index: number) => {
		setDraft(prev => ({
			...prev,
			emails: prev.emails.length === 1 ? [''] : prev.emails.filter((_, i) => i !== index),
		}));
	};

	const addEmail = () => {
		setDraft(prev => ({ ...prev, emails: [...prev.emails, ''] }));
	};

	const updatePhone = (index: number, patch: Partial<ContactPhoneDraft>) => {
		setDraft(prev => ({
			...prev,
			phones: prev.phones.map((p, i) => (i === index ? { ...p, ...patch } : p)),
		}));
	};

	const removePhone = (index: number) => {
		setDraft(prev => ({
			...prev,
			phones:
				prev.phones.length === 1 ? [{ value: '', label: DEFAULT_LABEL }] : prev.phones.filter((_, i) => i !== index),
		}));
	};

	const addPhone = () => {
		setDraft(prev => ({ ...prev, phones: [...prev.phones, { value: '', label: DEFAULT_LABEL }] }));
	};

	const handleSave = () => {
		if (!canSave) return;
		// Trim everything before handing off; keep only emails/phones with a non-empty value.
		const cleaned: ContactDraft = {
			firstName: draft.firstName.trim(),
			lastName: draft.lastName.trim(),
			jobTitle: draft.jobTitle.trim(),
			emails: draft.emails
				.map(e => e.trim())
				.filter((e, i, all) => e.length > 0 && all.findIndex(o => o.toLowerCase() === e.toLowerCase()) === i),
			phones: draft.phones.map(p => ({ value: p.value.trim(), label: p.label })).filter(p => p.value.length > 0),
		};
		onSave(cleaned);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<div className={css(styles.row2)}>
						<div className={css(styles.field)}>
							<label htmlFor='focused-contact-first' className={css(styles.label)}>
								First name
							</label>
							<Input
								id='focused-contact-first'
								value={draft.firstName}
								onChange={e => setDraft(prev => ({ ...prev, firstName: e.target.value }))}
								disabled={saving}
								autoFocus={mode === 'add'}
							/>
						</div>
						<div className={css(styles.field)}>
							<label htmlFor='focused-contact-last' className={css(styles.label)}>
								Last name
							</label>
							<Input
								id='focused-contact-last'
								value={draft.lastName}
								onChange={e => setDraft(prev => ({ ...prev, lastName: e.target.value }))}
								disabled={saving}
							/>
						</div>
					</div>
					<div className={css(styles.field)}>
						<label htmlFor='focused-contact-title' className={css(styles.label)}>
							Title
						</label>
						<Input
							id='focused-contact-title'
							value={draft.jobTitle}
							onChange={e => setDraft(prev => ({ ...prev, jobTitle: e.target.value }))}
							disabled={saving}
						/>
					</div>
					<div className={css(styles.field)}>
						<span className={css(styles.label)}>Email addresses</span>
						<div className={css(styles.list)}>
							{draft.emails.map((email, index) => (
								<div key={index} className={css(styles.row)}>
									<Input
										type='email'
										value={email}
										onChange={e => updateEmail(index, e.target.value)}
										placeholder='name@example.com'
										disabled={saving}
										styles={[styles.rowInput]}
										aria-label={`Email address ${index + 1}`}
									/>
									<Button
										type='button'
										variant='ghost'
										size='icon'
										title='Remove email address'
										aria-label='Remove email address'
										onClick={() => removeEmail(index)}
										disabled={saving}
									>
										<IconX size={14} />
									</Button>
								</div>
							))}
						</div>
						<Button
							type='button'
							variant='outline'
							size='sm'
							styles={[styles.addBtn]}
							onClick={addEmail}
							disabled={saving}
						>
							<IconPlus size={14} />
							Add email address
						</Button>
					</div>
					<div className={css(styles.field)}>
						<span className={css(styles.label)}>Phone numbers</span>
						<div className={css(styles.list)}>
							{draft.phones.map((phone, index) => (
								<div key={index} className={css(styles.row)}>
									<Input
										value={phone.value}
										onChange={e => updatePhone(index, { value: e.target.value })}
										placeholder='(555) 555-0142'
										disabled={saving}
										styles={[styles.rowInput]}
									/>
									<select
										value={phone.label}
										onChange={e => updatePhone(index, { label: e.target.value })}
										disabled={saving}
										className={css(styles.select)}
										aria-label='Phone number label'
									>
										{PHONE_LABELS.map(l => (
											<option key={l} value={l}>
												{l}
											</option>
										))}
									</select>
									<Button
										type='button'
										variant='ghost'
										size='icon'
										title='Remove phone number'
										aria-label='Remove phone number'
										onClick={() => removePhone(index)}
										disabled={saving}
									>
										<IconX size={14} />
									</Button>
								</div>
							))}
						</div>
						<Button
							type='button'
							variant='outline'
							size='sm'
							styles={[styles.addBtn]}
							onClick={addPhone}
							disabled={saving}
						>
							<IconPlus size={14} />
							Add phone number
						</Button>
					</div>
				</DialogBody>
				<DialogFooter>
					<Button variant='outline' onClick={() => onOpenChange(false)} disabled={saving}>
						Cancel
					</Button>
					<Button variant='primary' onClick={handleSave} disabled={!canSave}>
						{saving ? 'Saving…' : submitLabel}
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

const styles = StyleSheet.create({
	field: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[8],
		marginBottom: SPACE[16],
	},
	row2: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: SPACE[12],
	},
	label: {
		fontSize: 12,
		fontWeight: 500,
		color: 'var(--ink-muted)',
	},
	list: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[8],
	},
	row: {
		display: 'flex',
		gap: SPACE[8],
		alignItems: 'center',
	},
	rowInput: {
		flex: 1,
		minWidth: 0,
	},
	select: {
		height: 36,
		padding: '0 8px',
		borderRadius: 6,
		border: '1px solid var(--border)',
		background: 'var(--bg)',
		color: 'var(--ink)',
		fontSize: 13,
	},
	addBtn: {
		alignSelf: 'flex-start',
	},
});
